import React from "react";
import { Megaphone } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSiteConfig } from "@/context/SiteConfigContext";

// Renders the admin-configurable announcement content for the top strip.
// Returns null when disabled, outside the date window, or hidden for the viewport.
export default function AnnouncementBar() {
  const { config } = useSiteConfig();
  const { t } = useTranslation();
  const c = config || {};
  if (!c.ann_enabled) return null;
  if (!c.ann_desktop && !c.ann_mobile) return null;

  const now = new Date();
  if (c.ann_start && now < new Date(c.ann_start)) return null;
  if (c.ann_end && now > new Date(`${c.ann_end}T23:59:59`)) return null;

  // Admin-entered English is the source; reviewed translations come from the i18n store
  // (only approved + current rows are served, so changed English falls back to English).
  const tr = (key, s) => (s ? t(key, { defaultValue: s }) : s);
  const text = tr("announce.text", (c.ann_text || "").trim());
  const mobileText = tr("announce.textMobile", (c.ann_text_mobile || "").trim()) || text;
  if (!text) return null;

  let vis = "flex";
  if (!c.ann_desktop) vis = "flex sm:hidden";
  else if (!c.ann_mobile) vis = "hidden sm:flex";

  return (
    <div className={`${vis} items-center justify-center gap-2.5 min-w-0`} data-testid="announcement-bar">
      {c.ann_prefix ? (
        <span className="hidden sm:inline-flex items-center gap-1 rounded-sm bg-orange-500/20 text-orange-300 px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.18em] shrink-0">
          <Megaphone className="h-3 w-3" /> {tr("announce.prefix", c.ann_prefix)}
        </span>
      ) : null}
      <span className="text-xs text-primary-foreground/90 truncate">
        <span className="hidden sm:inline">{text}</span>
        <span className="sm:hidden">{mobileText}</span>
      </span>
      {c.ann_link ? (
        <a href={c.ann_link} target={c.ann_link_newtab ? "_blank" : "_self"} rel="noreferrer" className="text-xs font-medium text-orange-300 hover:text-orange-200 whitespace-nowrap transition-colors shrink-0" data-testid="announcement-link">
          {tr("announce.linkText", (c.ann_link_text || "").trim()) || t("common.learnMore")} →
        </a>
      ) : null}
    </div>
  );
}
